class AppDownloadRoot extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-app-search-input]');
    this.form = this.querySelector('[data-app-search-form]');
    this.main = this.querySelector('.app-download__main');
    this.nav = this.querySelector('[data-app-nav]');
    this.navCategories = this.querySelector('[data-app-nav-categories]');
    this.chevronTemplate = this.querySelector('[data-app-nav-chevron-template]');
    this.activePath = 'all';
    this.lastFilterSig = '';
    this.hasAppliedOnce = false;

    this.boundFilterClick = this.onFilterClick.bind(this);
    this.boundSectionLoad = this.onSectionLoad.bind(this);
    this.boundBlockSelect = this.onBlockSelect.bind(this);
    this.nav?.addEventListener('click', this.boundFilterClick);
    document.addEventListener('shopify:section:load', this.boundSectionLoad);
    document.addEventListener('shopify:block:select', this.boundBlockSelect);

    this.input?.addEventListener('input', () => this.applyFilters());
    this.input?.addEventListener('search', () => this.applyFilters());
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.applyFilters();
    });

    this.boundCardClick = (e) => {
      if (e.target.closest('[data-app-card][data-app-no-href]')) {
        e.preventDefault();
      }
    };
    this.addEventListener('click', this.boundCardClick);

    this.refresh();
  }

  disconnectedCallback() {
    this.nav?.removeEventListener('click', this.boundFilterClick);
    document.removeEventListener('shopify:section:load', this.boundSectionLoad);
    document.removeEventListener('shopify:block:select', this.boundBlockSelect);
    this.removeEventListener('click', this.boundCardClick);
  }

  onSectionLoad(e) {
    if (!e.target.contains(this)) return;
    this.refresh();
  }

  onBlockSelect() {
    requestAnimationFrame(() => this.refresh());
  }

  refresh() {
    const tree = this.assignPaths();
    this.buildNav(tree);
    this.groups = Array.from(this.querySelectorAll('[data-app-group]'));
    this.cards = Array.from(this.querySelectorAll('[data-app-card]'));
    this.applyFilters();
  }

  slug(s) {
    return String(s)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Walk Category > Subcategory in the DOM, compute the filter path for each
  // group + its cards, and return the tree used to render the sidebar nav.
  assignPaths() {
    const tree = [];

    this.querySelectorAll('[data-app-category]').forEach((cat) => {
      const catTitle = (cat.dataset.appCategoryTitle || '').trim();
      const cslug = this.slug(catTitle);
      const subs = [];

      cat.querySelectorAll('[data-app-group]').forEach((group) => {
        const subTitle = (group.dataset.appSubcategoryTitle || group.querySelector('.app-download__group-title')?.textContent || '').trim();
        const path = `${cslug}/${this.slug(subTitle)}`;
        group.dataset.appPath = path;
        group.querySelectorAll('[data-app-card]').forEach((card) => {
          card.dataset.appPath = path;
        });
        if (!subs.some((entry) => entry.path === path)) {
          subs.push({ path, subTitle: subTitle || path });
        }
      });

      if (catTitle && subs.length) {
        tree.push({ catTitle, subs });
      }
    });

    return tree;
  }

  buildNav(tree) {
    if (!this.navCategories) return;

    this.navCategories.replaceChildren();
    let firstOpen = true;

    tree.forEach(({ catTitle, subs }) => {
      const details = document.createElement('details');
      details.className = 'app-download__nav-details';
      if (firstOpen) {
        details.open = true;
        firstOpen = false;
      }

      const summary = document.createElement('summary');
      summary.className = 'app-download__nav-summary';

      const title = document.createElement('span');
      title.textContent = catTitle;
      summary.appendChild(title);

      if (this.chevronTemplate?.content) {
        summary.appendChild(this.chevronTemplate.content.cloneNode(true));
      }

      const list = document.createElement('div');
      list.className = 'app-download__nav-list';

      subs.forEach(({ path, subTitle }) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'app-download__nav-btn';
        btn.dataset.appFilter = path;
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = subTitle;
        list.appendChild(btn);
      });

      details.append(summary, list);
      this.navCategories.appendChild(details);
    });
  }

  onFilterClick(e) {
    const btn = e.target.closest('[data-app-filter]');
    if (!btn || !this.nav?.contains(btn)) return;
    e.preventDefault();
    this.activePath = btn.getAttribute('data-app-filter') || 'all';
    this.nav.querySelectorAll('[data-app-filter]').forEach((b) => {
      const on = b.getAttribute('data-app-filter') === this.activePath;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.applyFilters();
  }

  normalize(s) {
    return String(s)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  getQuery() {
    return this.normalize(this.input?.value || '');
  }

  prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }

  applyFilters() {
    const q = this.getQuery();
    const sig = `${this.activePath}|${q}`;
    const reduce = this.prefersReducedMotion();
    const shouldAnimate = !reduce && this.hasAppliedOnce && sig !== this.lastFilterSig;
    const useViewTransition =
      !reduce &&
      shouldAnimate &&
      this.main &&
      typeof document.startViewTransition === 'function';

    const run = () => {
      this.cards.forEach((card) => {
        const hay = this.normalize(card.getAttribute('data-search') || '');
        const matchQ = !q || hay.includes(q);
        const path = card.getAttribute('data-app-path') || '';
        const matchC = this.activePath === 'all' || path === this.activePath;
        const show = matchQ && matchC;
        card.hidden = !show;
        card.classList.remove('app-download__card--enter');
        card.style.removeProperty('--ad-stagger');
        if (show) {
          card.removeAttribute('aria-hidden');
        } else {
          card.setAttribute('aria-hidden', 'true');
        }
      });

      this.groups.forEach((group) => {
        const any = Array.from(group.querySelectorAll('[data-app-card]')).some((c) => !c.hidden);
        const wasHidden = group.hidden;
        group.hidden = !any;
        group.classList.remove('app-download__group--enter');
        if (any) {
          group.removeAttribute('aria-hidden');
          if (shouldAnimate && wasHidden && !useViewTransition) {
            void group.offsetWidth;
            group.classList.add('app-download__group--enter');
          }
        } else {
          group.setAttribute('aria-hidden', 'true');
        }
      });

      const empty = this.querySelector('[data-app-empty]');
      if (empty) {
        const anyCard = this.cards.some((c) => !c.hidden);
        empty.hidden = anyCard || this.cards.length === 0;
      }

      if (shouldAnimate && !useViewTransition) {
        requestAnimationFrame(() => {
          let i = 0;
          this.cards.forEach((card) => {
            if (card.hidden) return;
            card.style.setProperty('--ad-stagger', `${Math.min(i, 16) * 32}ms`);
            card.classList.add('app-download__card--enter');
            i += 1;
          });
        });
      }

      this.lastFilterSig = sig;
      this.hasAppliedOnce = true;
    };

    if (useViewTransition) {
      document.startViewTransition(run);
    } else {
      run();
    }
  }
}

if (!customElements.get('app-download-root')) {
  customElements.define('app-download-root', AppDownloadRoot);
}
